import { Card, CardBody, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@heroui/react';
import NextLink from 'next/link';

import { ComparisonPageLayout } from '../../components/comparisons/ComparisonPageLayout';
import {
  ComparisonLinkList,
  FaqList,
  KreatliCostTable,
  makeHeroBlok,
  SeatCostTable,
  SummaryBox,
} from '../../components/comparisons/CompetitorPricingPage';
import { Section } from '../../components/layout/Storyblok/Section/Section';
import wysiwygStyles from '../../components/layout/Storyblok/Wysiwyg/Wysiwyg.module.scss';
import { formatIsoDate, LastVerifiedBadge } from '../../components/pseo/LastVerifiedBadge';
import { CTASection } from '../../components/shared/CTASection';
import { FAQStructuredData } from '../../components/shared/FAQStructuredData';
import { FRAME_IO_FREE_PLAN_LIMITS_PATH, getCompetitorLinks } from '../../data/competitor-comparisons';
import {
  COMPETITOR_PRICING_MAX_AGE_DAYS,
  formatPricingAmount,
  getCompetitorPricing,
  getMonthlyCostForSeats,
} from '../../data/competitor-pricing';
import { EXPLORATION_MODE, getKreatliPlan, TRIAL_DAYS } from '../../data/kreatli-plans';
import { warnStaleEntries } from '../../data/pseo/staleness';
import type { SourceRef } from '../../data/pseo/types';
import { isPublishable } from '../../data/pseo/types';
import { useSession } from '../../hooks/useSession';
import { getHeadingId } from '../../utils/storyblok';

const DOCUMENT_TITLE = 'Frame.io Free Plan Limits (2026): What You Get';
const META_DESCRIPTION =
  'Frame.io free plan limits: 2 members, 2GB (+2GB mounted), 2 projects and 1 workspace, what happens over the limit, and what upgrading costs.';
const PUBLISH_DATE = '2026-10-22 00:00';
const READ_TIME = '6';
const COVER_SRC =
  'https://a.storyblok.com/f/287637539865613/2250x750/7b1d4796a1/kreatli-vs-frame-io-2026-the-freshest-most-practical-comparison-for-creative-teams.png';
const COVER_ALT = 'Frame.io free plan limits';
const UPGRADE_SEAT_COUNTS = [2, 3, 5, 10, 15] as const;

const entry = getCompetitorPricing('frame-io');
const freePlan = entry.plans.find((plan) => plan.isFree);
const proPlan = entry.plans.find((plan) => plan.name === 'Pro');
const teamPlan = entry.plans.find((plan) => plan.name === 'Team');
const creator = getKreatliPlan('creator');
const usd = (amount: number) => formatPricingAmount(amount, 'USD');
const twoOnPro = getMonthlyCostForSeats(entry, 2);

const PLAN_LIMITS_HELP: SourceRef = {
  label: 'Frame.io help: plan limits',
  url: 'https://help.frame.io/en/articles/2673548-plan-limits-in-frame-io',
};
const ADOBE_CC_HELP: SourceRef = {
  label: 'Adobe help: Frame.io for Creative Cloud',
  url: 'https://helpx.adobe.com/ca/business/enterprise/products-entitlements/frame-io-integration/frame-io-cc-business.html',
};
const SOURCES: SourceRef[] = [{ label: 'Frame.io pricing', url: 'https://www.frame.io/pricing' }, PLAN_LIMITS_HELP, ADOBE_CC_HELP];

const TOC_SECTIONS = [
  'Frame.io free plan limits at a glance',
  'What happens when you go over the limits',
  'The Adobe Creative Cloud free allowance',
  'What upgrading from the free plan costs',
  'Frame.io Free vs Kreatli Exploration',
  "When Frame.io's free plan is the better choice",
  'Related pricing and comparisons',
  'Sources',
  'FAQ: Frame.io free plan',
] as const;

const LIMIT_ROWS = [
  { label: 'Price', value: '$0' },
  { label: 'Members', value: freePlan?.seats ?? 'Up to 2 members' },
  { label: 'Storage', value: freePlan?.storage ?? '2GB (+2GB mounted)' },
  { label: 'Projects', value: '2' },
  { label: 'Workspaces', value: '1' },
  { label: 'Reviewers', value: freePlan?.reviewers ?? 'Share reviewers need no seat' },
  { label: 'Over the limit', value: '"Project limit exceeded" banner; the account may be locked after a grace period' },
];

const VS_KREATLI_ROWS = [
  { label: 'Price', frame: '$0', kreatli: '$0' },
  {
    label: 'Projects and files',
    frame: '2 projects, 1 workspace, 2GB storage (+2GB mounted)',
    kreatli: `${EXPLORATION_MODE.projectLimit} project, ${EXPLORATION_MODE.fileLimit} files`,
  },
  { label: 'Credit card', frame: 'Not stated for Free', kreatli: 'Not needed' },
  { label: 'Guest reviewers', frame: 'Share reviewers need no seat', kreatli: 'Guest review links, no login (name only)' },
  {
    label: 'First paid step',
    frame: proPlan ? `Pro: ${usd(proPlan.priceMonthly ?? 0)} per member/month, up to 5 members` : 'Pro',
    kreatli: [
      `${creator.name}: ${usd(creator.priceMonthly ?? 0)} per user/month, up to ${creator.memberLimit} members, `,
      `${TRIAL_DAYS}-day trial`,
    ].join(''),
  },
];

const FAQS = [
  {
    question: 'What are the limits of the Frame.io free plan?',
    answer:
      'The Frame.io Free plan allows up to 2 members, 2GB of storage (+2GB mounted), 2 projects and 1 workspace. Share reviewers don’t need a seat.',
  },
  {
    question: 'What happens if I go over the Frame.io free plan limits?',
    answer:
      'Frame.io shows a "Project limit exceeded (Free plan only)" banner when an account is over the limit, and says the account may be locked after a grace period.',
  },
  {
    question: 'Do Adobe Creative Cloud users get more on Frame.io for free?',
    answer:
      'Yes. Adobe says Creative Cloud users who sign up for Frame.io with their Adobe credentials get 100GB of storage and up to 5 projects.',
  },
  {
    question: 'How much does it cost to upgrade from Frame.io Free?',
    answer: [
      `Pro is ${usd(proPlan?.priceMonthly ?? 0)} per member per month for up to 5 members, and Team is `,
      `${usd(teamPlan?.priceMonthly ?? 0)} per member per month for up to 15, plus tax. `,
      `Keeping 2 members on Pro costs `,
      `${twoOnPro.total === null ? 'a custom price' : usd(twoOnPro.total)} per month.`,
    ].join(''),
  },
  {
    question: 'Is there a free alternative to the Frame.io free plan?',
    answer: [
      `Kreatli's ${EXPLORATION_MODE.name} mode is free with ${EXPLORATION_MODE.label}, which is smaller than `,
      `Frame.io Free. Paid Kreatli plans start with a ${TRIAL_DAYS}-day trial; ${creator.name} is `,
      `${usd(creator.priceMonthly ?? 0)} per user per month for up to ${creator.memberLimit} members, `,
      `with guest review links on every plan.`,
    ].join(''),
  },
];

export default function FrameIoFreePlanLimitsPage() {
  useSession();

  const tocLinks = TOC_SECTIONS.map((label) => ({ label, url: `#${getHeadingId(label)}` }));
  const links = getCompetitorLinks(['frame-io'], FRAME_IO_FREE_PLAN_LIMITS_PATH);

  return (
    <>
      <FAQStructuredData faqs={FAQS} />
      <ComparisonPageLayout
        documentTitle={DOCUMENT_TITLE}
        metaDescription={META_DESCRIPTION}
        canonicalPath={FRAME_IO_FREE_PLAN_LIMITS_PATH}
        ogImageUrl={COVER_SRC}
        ogImageAlt={COVER_ALT}
        publishDate={PUBLISH_DATE}
        readTimeMinutes={READ_TIME}
        breadcrumbLabel="Frame.io free plan limits"
        tocLinks={tocLinks}
      >
        <Section
          blok={makeHeroBlok({
            id: 'frame-io-free-plan-limits',
            title: 'Frame.io Free Plan Limits',
            text: 'What the Frame.io free plan includes, what happens when you hit its limits, and what it costs to move up.',
            coverSrc: COVER_SRC,
            coverAlt: COVER_ALT,
          })}
        />

        <SummaryBox>
          <p>
            Frame.io Free covers 2 members, 2 projects, 1 workspace and 2GB of storage (+2GB mounted). Go over the
            project limit and you get a banner, then a possible account lock after a grace period. The next step up is
            Pro at {usd(proPlan?.priceMonthly ?? 0)} per member per month. Checked {formatIsoDate(entry.lastVerified)}{' '}
            against Frame.io&apos;s pricing page and help center.
          </p>
        </SummaryBox>

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(TOC_SECTIONS[0])}>{TOC_SECTIONS[0]}</h2>
        </div>

        <Card className="border border-foreground-200 bg-content1/60 backdrop-blur-lg">
          <CardBody className="overflow-x-auto p-2 sm:p-4">
            <Table aria-label="Frame.io free plan limits" removeWrapper className="min-w-[520px]">
              <TableHeader>
                <TableColumn>Limit</TableColumn>
                <TableColumn>Frame.io Free</TableColumn>
              </TableHeader>
              <TableBody>
                {LIMIT_ROWS.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell className="font-semibold text-foreground-700">{row.label}</TableCell>
                    <TableCell>{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(TOC_SECTIONS[1])}>{TOC_SECTIONS[1]}</h2>
          <p>
            Frame.io&apos;s plan-limits article says an account over the free limits shows a{' '}
            <strong>&quot;Project limit exceeded (Free plan only)&quot;</strong> banner. If you stay over the limit,
            the account may be locked after a grace period.
          </p>
          <p>
            The Free plan allows up to 2 members, so a third member means a paid plan. Share reviewers don&apos;t need
            a seat.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[2])}>{TOC_SECTIONS[2]}</h2>
          <p>
            Adobe Creative Cloud users who sign up for Frame.io with their Adobe credentials get a bigger free
            allowance: <strong>100GB of storage and up to 5 projects</strong>, according to Adobe&apos;s help page. If
            you already pay for Creative Cloud, check which sign-up you used before you upgrade.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[3])}>{TOC_SECTIONS[3]}</h2>
          <p>
            Monthly list prices before tax on the cheapest paid plan that fits. Pro covers up to 5 members and Team up
            to 15; above that, Enterprise has custom pricing. Annual billing saves 13%, but Frame.io doesn&apos;t show
            the annual price.
          </p>
        </div>

        <SeatCostTable entry={entry} seatCounts={UPGRADE_SEAT_COUNTS} />

        <div className={wysiwygStyles.wysiwyg}>
          <p>
            Full plan details are on the <NextLink href="/comparisons/frame-io-pricing">Frame.io pricing</NextLink>{' '}
            breakdown.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[4])}>{TOC_SECTIONS[4]}</h2>
          <p>
            Kreatli&apos;s free {EXPLORATION_MODE.name} mode is for trying the review workflow, not for running ongoing
            work. Here is how the two free options and their first paid steps compare:
          </p>
        </div>

        <Card className="border border-foreground-200 bg-content1/60 backdrop-blur-lg">
          <CardBody className="overflow-x-auto p-2 sm:p-4">
            <Table aria-label="Frame.io Free compared with Kreatli Exploration" removeWrapper className="min-w-[640px]">
              <TableHeader>
                <TableColumn> </TableColumn>
                <TableColumn>Frame.io Free</TableColumn>
                <TableColumn>{`Kreatli ${EXPLORATION_MODE.name}`}</TableColumn>
              </TableHeader>
              <TableBody>
                {VS_KREATLI_ROWS.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell className="font-semibold text-foreground-700">{row.label}</TableCell>
                    <TableCell>{row.frame}</TableCell>
                    <TableCell>{row.kreatli}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        <div className={wysiwygStyles.wysiwyg}>
          <p>Once you pay, here is the same team on each tool:</p>
        </div>

        <KreatliCostTable entry={entry} seatCounts={UPGRADE_SEAT_COUNTS} />

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(TOC_SECTIONS[5])}>{TOC_SECTIONS[5]}</h2>
          <ul>
            <li>
              <strong>You want to keep working for free.</strong> Frame.io Free gives 2 members 2 projects and 2GB;
              Kreatli {EXPLORATION_MODE.name} stops at {EXPLORATION_MODE.projectLimit} project and{' '}
              {EXPLORATION_MODE.fileLimit} files.
            </li>
            <li>
              <strong>You already pay for Creative Cloud.</strong> Signing up with Adobe credentials raises the free
              allowance to 100GB and 5 projects.
            </li>
            <li>
              <strong>You edit in Premiere Pro.</strong> Frame.io has a V4 panel for Premiere Pro 25.6 and later;
              Kreatli has no editing-software panels.
            </li>
            <li>
              <strong>You need to export comments.</strong> Frame.io exports comments to CSV, plain text and PDF;
              Kreatli doesn&apos;t export comments.
            </li>
          </ul>
          <p>
            If you&apos;re past the free plan and review with clients, compare{' '}
            <NextLink href="/platform/review-approval">Kreatli&apos;s video review software</NextLink> on one real
            round.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[6])}>{TOC_SECTIONS[6]}</h2>
          <ComparisonLinkList links={links} />

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[7])}>{TOC_SECTIONS[7]}</h2>
        </div>

        <LastVerifiedBadge lastVerified={entry.lastVerified} sources={SOURCES} changelog={entry.changelog} />

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(TOC_SECTIONS[8])}>{TOC_SECTIONS[8]}</h2>
        </div>

        <FaqList faqs={FAQS} />

        <CTASection
          title="Outgrown a free review plan?"
          description={`Explore Kreatli free (${EXPLORATION_MODE.label}), then start a ${TRIAL_DAYS}-day trial.`}
          primaryButtonText="Start for Free"
          primaryButtonHref="/sign-up"
        />
      </ComparisonPageLayout>
    </>
  );
}

export function getStaticProps() {
  warnStaleEntries('competitor-pricing', [entry], COMPETITOR_PRICING_MAX_AGE_DAYS);
  if (!isPublishable(entry)) {
    return { notFound: true };
  }
  return { props: {} };
}
